import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Button,
  Chip,
  CircularProgress,
  Fade,
  useTheme,
  alpha
} from '@mui/material';
import {
  History as HistoryIcon,
  Search as SearchIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Assignment as ActivityIcon,
  Schedule as TimeIcon,
  Save as SaveIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import styled from 'styled-components';
import { apiCall } from '../../../services/ApiCall.js';
import Loader from './Loader.js';
import config from "../../../config/config.json";
import { useNavigate } from 'react-router-dom';

// Styled Components with #0d6efd theme
const StyledContainer = styled(Box)`
  background: linear-gradient(135deg, #f8faff 0%, #e3f2fd 100%);
  min-height: 100vh;
  padding: 24px;
`;

const HeaderCard = styled(Card)`
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px);
  border-radius: 20px !important;
  box-shadow: 0 8px 32px rgba(13, 110, 253, 0.12) !important;
  border: 1px solid rgba(13, 110, 253, 0.1);
  margin-bottom: 24px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
  }
`;

const TableCard = styled(Card)`
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px);
  border-radius: 20px !important;
  box-shadow: 0 8px 32px rgba(13, 110, 253, 0.1) !important;
  border: 1px solid rgba(13, 110, 253, 0.08);
  overflow: hidden;
`;

const StyledTextField = styled(TextField)`
  .MuiOutlinedInput-root {
    border-radius: 12px !important;
    background: rgba(255, 255, 255, 0.9);
    transition: all 0.3s ease !important;
    
    &:hover {
      background: rgba(255, 255, 255, 1);
      box-shadow: 0 4px 12px rgba(13, 110, 253, 0.1);
    }
    
    &.Mui-focused {
      background: rgba(255, 255, 255, 1);
      box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.2);
    }
  }
  
  .MuiInputLabel-root {
    font-weight: 500;
    
    &.Mui-focused {
      color: #0d6efd;
    }
  }
`;

const StyledTableContainer = styled(TableContainer)`
  border-radius: 16px !important;
  
  .MuiTable-root {
    background: transparent;
  }
`;

const StyledTableHead = styled(TableHead)`
  .MuiTableCell-head {
    background: #0a58ca !important;
    backdrop-filter: blur(20px);
    color: #1a202c !important;
    font-weight: 700 !important;
    font-size: 13px !important;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 16px !important;
    border-bottom: 2px solid rgba(13, 110, 253, 0.1) !important;
    border-top: none !important;
    position: relative;
    cursor: pointer;
    transition: all 0.3s ease !important;
    
    &:hover {
      background: rgba(248, 250, 255, 0.9) !important;
      color: #0d6efd !important;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(13, 110, 253, 0.08);
      
      .header-icon {
        transform: scale(1.1);
        color: #0d6efd !important;
      }
      
      .header-text {
        color: #0d6efd !important;
      }
    }
    
    &:first-of-type {
      border-top-left-radius: 16px;
    }
    
    &:last-of-type {
      border-top-right-radius: 16px;
    }
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      width: 0;
      height: 2px;
      background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
      transition: all 0.3s ease;
      transform: translateX(-50%);
    }
    
    &:hover::after {
      width: 80%;
    }
  }
`;

const HeaderContent = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  
  .header-icon {
    transition: all 0.3s ease;
    color: white;
    font-size: 18px;
  }
  
  .header-text {
    transition: all 0.3s ease;
    color: white;
    font-weight: 700;
    letter-spacing: 0.3px;
  }
`;

const StyledTableRow = styled(TableRow)`
  transition: all 0.2s ease !important;
  
  &:hover {
    background: rgba(13, 110, 253, 0.04) !important;
    transform: scale(1.001);
  }
  
  &:nth-of-type(even) {
    background: rgba(248, 250, 255, 0.5);
  }
`;

const StyledTableCell = styled(TableCell)`
  padding: 16px !important;
  border-bottom: 1px solid rgba(13, 110, 253, 0.1) !important;
  color: #1a202c !important;
  font-weight: 500 !important;
  font-size: 14px !important;
`;

const EditableInput = styled(TextField)`
  .MuiOutlinedInput-root {
    border-radius: 8px !important;
    background: rgba(255, 255, 255, 0.9);
    font-size: 14px !important;
    
    &:hover {
      background: rgba(255, 255, 255, 1);
    }
    
    &.Mui-focused {
      background: rgba(255, 255, 255, 1);
      box-shadow: 0 0 0 1px rgba(13, 110, 253, 0.3);
    }
  }
`;

const SaveButton = styled(Button)`
  background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%) !important;
  border-radius: 8px !important;
  padding: 8px 16px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  transition: all 0.3s ease !important;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 15px rgba(13, 110, 253, 0.3) !important;
  }
`;

const ActivityChip = styled(Chip)`
  background: ${props => getActivityColor(props.activity).background} !important;
  color: ${props => getActivityColor(props.activity).color} !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
`;

const LoaderContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 20px;
  backdrop-filter: blur(10px);
`;

// Helper function for activity colors
const getActivityColor = (activity) => {
  const activityLower = activity?.toLowerCase() || '';
  
  if (activityLower.includes('create') || activityLower.includes('add')) {
    return { background: alpha('#4caf50', 0.1), color: '#4caf50' };
  } else if (activityLower.includes('update') || activityLower.includes('edit')) {
    return { background: alpha('#ff9800', 0.1), color: '#ff9800' };
  } else if (activityLower.includes('delete') || activityLower.includes('remove')) {
    return { background: alpha('#f44336', 0.1), color: '#f44336' };
  } else {
    return { background: alpha('#0d6efd', 0.1), color: '#0d6efd' };
  }
};

const CompanyHistory = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  
  // Original state variables - keeping exactly the same
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editRowIndex, setEditRowIndex] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  // Original functions - keeping exactly the same logic
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value,
    });
  };

  const handleSaveClick = () => {
    const newData = [...data];
    newData[editRowIndex] = editFormData;
    setData(newData);
    setEditRowIndex(null);
  };

  const getCompanyHistoryList = async () => {
    setLoading(true);
    const { isSuccess, data } = await apiCall(
      `${config.POST_LOGIN_API_URL}getCompanyHistoryList`,
      {},
      {},
      "GET"
    );
    setLoading(false);
    if (isSuccess) {
      setData(data?.data?.reverse());
    }
  };

  // Original useEffect - keeping exactly the same
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
    }
    getCompanyHistoryList();
  }, []);

  // Original filtering logic - keeping exactly the same
  const filteredData = data.filter(item => {
    const fullAddress = `${item?.address ?? ''} ${item?.city ?? ''} ${item?.state ?? ''}`.toLowerCase();
    const searchLower = searchQuery.toLowerCase();
    return (
      item?.companyName?.toLowerCase().includes(searchLower) ||
      fullAddress.includes(searchLower) ||
      item?.name?.toLowerCase().includes(searchLower) ||
      item?.activity?.toLowerCase().includes(searchLower)
    );
  });

  // Original date formatting - keeping exactly the same
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
  
    hours = hours % 12 || 12;
    hours = String(hours).padStart(2, '0');
  
    return `${month}-${day}-${year} ${hours}:${minutes} ${ampm}`;
  };

  // Original loader condition - keeping exactly the same
  if (loading) {
    return (
      <StyledContainer>
        <LoaderContainer>
          <CircularProgress 
            size={60} 
            sx={{ 
              color: '#0d6efd',
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              }
            }} 
          />
        </LoaderContainer>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer>
      {/* Header Section */}
      <HeaderCard>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <HistoryIcon sx={{ color: '#0d6efd', mr: 2, fontSize: 32 }} />
              <Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)',
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 0.5
                  }}
                >
                  Company History
                </Typography>
                <Typography variant="body1" sx={{ color: '#64748b', fontWeight: 500 }}>
                  Track all company-related activities and changes
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip 
                label={`${filteredData.length} Records`}
                sx={{ 
                  background: alpha('#0d6efd', 0.1),
                  color: '#0d6efd',
                  fontWeight: 600
                }}
              />
            </Box>
          </Box>
        </CardContent>
      </HeaderCard>

      {/* Search Section */}
      <Fade in timeout={500}>
        <Card sx={{ 
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          border: '1px solid rgba(13, 110, 253, 0.08)',
          mb: 3
        }}>
          <CardContent sx={{ p: 3 }}>
            <StyledTextField
              fullWidth
              placeholder="Search by Company Name, Address, Activity, or Name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#64748b' }} />
                  </InputAdornment>
                ),
              }}
            />
          </CardContent>
        </Card>
      </Fade>

      {/* Table Section */}
      <Fade in timeout={700}>
        <TableCard>
          <StyledTableContainer>
            <Table stickyHeader>
              <StyledTableHead>
                <TableRow>
                  <TableCell>
                    <HeaderContent>
                      <BusinessIcon className="header-icon" />
                      <span className="header-text">Company</span>
                    </HeaderContent>
                  </TableCell>
                  <TableCell>
                    <HeaderContent>
                      <LocationIcon className="header-icon" />
                      <span className="header-text">Location</span>
                    </HeaderContent>
                  </TableCell>
                  <TableCell>
                    <HeaderContent>
                      <PersonIcon className="header-icon" />
                      <span className="header-text">User</span>
                    </HeaderContent>
                  </TableCell>
                  <TableCell>
                    <HeaderContent>
                      <ActivityIcon className="header-icon" />
                      <span className="header-text">Action</span>
                    </HeaderContent>
                  </TableCell>
                  <TableCell>
                    <HeaderContent>
                      <TimeIcon className="header-icon" />
                      <span className="header-text">When</span>
                    </HeaderContent>
                  </TableCell>
                </TableRow>
              </StyledTableHead>
              
              <TableBody>
                {filteredData.map((item, index) => (
                  <StyledTableRow key={index}>
                    {editRowIndex === index ? (
                      <>
                        <StyledTableCell>
                          <EditableInput
                            size="small"
                            name="companyName"
                            value={editFormData.companyName}
                            onChange={handleInputChange}
                            fullWidth
                          />
                        </StyledTableCell>
                        <StyledTableCell>
                          <EditableInput
                            size="small"
                            name="address"
                            value={editFormData.address}
                            onChange={handleInputChange}
                            fullWidth
                          />
                        </StyledTableCell>
                        <StyledTableCell>
                          <EditableInput
                            size="small"
                            name="activityBy"
                            value={editFormData.activityBy}
                            onChange={handleInputChange}
                            fullWidth
                          />
                        </StyledTableCell>
                        <StyledTableCell>
                          <EditableInput
                            size="small"
                            name="activity"
                            value={editFormData.activity}
                            onChange={handleInputChange}
                            fullWidth
                          />
                        </StyledTableCell>
                        <StyledTableCell>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <SaveButton
                              variant="contained"
                              size="small"
                              startIcon={<SaveIcon />}
                              onClick={handleSaveClick}
                            >
                              Save
                            </SaveButton>
                          </Box>
                        </StyledTableCell>
                      </>
                    ) : (
                      <>
                        <StyledTableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a202c' }}>
                            {item?.companyName}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Typography variant="body2" sx={{ color: '#64748b' }}>
                            {`${item?.address} ${item?.city} ${item?.state}`}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ color: '#0d6efd', fontSize: 16 }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {item?.name}
                            </Typography>
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell>
                          <ActivityChip
                            label={item?.activity}
                            size="small"
                            activity={item?.activity}
                          />
                        </StyledTableCell>
                        <StyledTableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#1a202c' }}>
                              {formatDateTime(item?.createdAt)}
                            </Typography>
                          </Box>
                        </StyledTableCell>
                      </>
                    )}
                  </StyledTableRow>
                ))}
                
                {filteredData.length === 0 && (
                  <StyledTableRow>
                    <StyledTableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" sx={{ color: '#64748b' }}>
                        No records found matching your search criteria
                      </Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </TableCard>
      </Fade>
    </StyledContainer>
  );
};

export default CompanyHistory;